// Utils
import { animateScrollPromise } from '../../../utils/animation/animateScrollIntoView';
import { length, prop } from '../../../../common/utils/immutableHelper';
import scrollExcessHandler from './scrollExcessHandler';
import { getTargetScrollToAlignElementToCenter, getTargetScrollToAlignElementToEdge } from './scrollToElementUtils';
import { getScrollConfigForAction } from './scrollToElementConfigUtils';
import { getScrollCleanupConfigForAction } from './scrollToElementCleanupConfigUtils';

// Selectors
import { selectIsMobileAppMode } from '../../../platform/platformSelector';
import { getSelectedElementIds } from '../../../element/selection/selectedElementsSelector';

// Constants
import { ELEMENT_EDIT_START } from '../../../../common/elements/selectionConstants';

// Types
import { UnknownAction } from 'redux';
import { ReduxStore } from '../../../types/reduxTypes';
import { SheetId } from '../../structural/sheet/sheetTypes';

/**
 * WEB-13214 - Determine whether the action should still be triggered.
 *
 * If, for example, an ELEMENT_EDIT_START action is triggered, we want to ensure that it hasn't been
 * followed by an ELEMENTS_DESELECT_ALL action while the scroll animation is in progress.
 * This might happen when the user triple taps on the element to edit - the third tap might fall outside
 * the element, and land on the canvas, which will deselect the element.
 * Previously, this would result in the element being deselected, but the edit action still being triggered,
 * resulting in a deselected element in the edit mode.
 */
const shouldTriggerAction = (action: UnknownAction, initialState: any, currentState: any): boolean => {
    if (action.type !== ELEMENT_EDIT_START) return true;

    // WEB-13214 - If the selected elements have changed since we started the scroll on edit and when
    //  we're going to trigger the action, we don't want to trigger the edit start action.
    const initialSelectedIds = getSelectedElementIds(initialState);
    const currentSelectedIds = getSelectedElementIds(currentState);

    if (initialSelectedIds === currentSelectedIds) return true;

    // If the size increases from 0 to 1, it might be an element that didn't need to start as selected
    // prior to editing, such as a comment thread.
    if (length(initialSelectedIds) !== 0 || length(currentSelectedIds) !== 1) return false;

    const nowSelectedElementId = prop(0, currentSelectedIds);
    return nowSelectedElementId === action.id;
};

/**
 * This middleware is responsible for:
 * - scrolling to the edited element on an edit start, label rename start, or element-specific sheet opening
 * - applying scroll excess padding to the bottom of the scrollable element to allow the bottom portion of the
 *   scrollable element to be accessible when the keyboard/sheet is shown, and resets it when the user finishes editing
 *
 * This needs to be in the middleware because we want to scroll to element before a caret is applied to the element,
 * which on Safari mobile web, will cause the whole webpage (not just canvas) to scroll to the caret position,
 * and cause the page to be misaligned.
 */
export default (store: ReduxStore) => (next: Function) => async (action: UnknownAction) => {
    if (action.remote) return next(action);

    const state = store.getState();

    // Get scroll config applied for certain actions, such as when user edits an element or keyboard/sheet shows up
    let scrollConfig = getScrollConfigForAction(state, action);

    // If bottomObstructionHeight defined, apply scroll excess padding to the bottom of the scrollable element
    // to allow the bottom portion of the scrollable element to be accessible
    if (scrollConfig?.bottomObstructionHeight) {
        const sheetId = action.sheetId as SheetId | undefined;
        await scrollExcessHandler.apply(scrollConfig, sheetId);
        // Another animation can move the target while scroll padding is being applied.
        scrollConfig = getScrollConfigForAction(state, action);
    }

    // If scrollTargetRect is defined, animate scroll to the element
    if (scrollConfig?.scrollTargetRect) {
        const { scrollableParent, scrollTargetRect } = scrollConfig;

        const getTargetScrollToElement = selectIsMobileAppMode(state)
            ? getTargetScrollToAlignElementToCenter
            : getTargetScrollToAlignElementToEdge;

        const targetScroll = getTargetScrollToElement({ ...scrollConfig, scrollTargetRect });

        await animateScrollPromise({
            scrollableParent,
            fromScrollLeft: scrollableParent.scrollLeft,
            fromScrollTop: scrollableParent.scrollTop,
            toScrollLeft: targetScroll.scrollLeft,
            toScrollTop: targetScroll.scrollTop,
            interpolationFactor: 0.2,
        });
    }

    const scrollCleanupConfig = getScrollCleanupConfigForAction(state, action);
    if (scrollCleanupConfig) {
        scrollExcessHandler.cleanup(scrollCleanupConfig);
    }

    // If something has changed from the start of this processing to now, we might no longer
    //  want to trigger the action.
    if (!shouldTriggerAction(action, state, store.getState())) return;

    next(action);
};
